(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else { root.SCPCartAdd = api; root.scpCartAddClient = api.install(root); }
})(typeof globalThis === 'object' ? globalThis : window, function () {
  'use strict';
  const propertyKey = properties => JSON.stringify(Object.entries(properties || {}).filter(([, value]) => value != null && value !== '').map(([key, value]) => [key, String(value)]).sort(([a], [b]) => a.localeCompare(b)));
  const planId = line => String(line.selling_plan_allocation?.selling_plan?.id || line.selling_plan || '');
  const validCart = cart => cart && Array.isArray(cart.items) && Number.isInteger(cart.item_count) && cart.item_count >= 0 && cart.items.every(item => item && String(item.variant_id || item.id || '') && Number.isInteger(item.quantity) && item.quantity >= 0 && (!item.properties || typeof item.properties === 'object'));
  function snapshot(body) {
    const properties = {}; let comparable = true;
    for (const [name, value] of body) {
      const match = /^properties\[(.*)\]$/.exec(name);
      if (match) { if (typeof value !== 'string') comparable = false; properties[match[1]] = value; }
    }
    return { id: String(body.get('id')), quantity: Number(body.get('quantity') || 1), selling_plan: body.get('selling_plan') || '', properties, comparable };
  }
  function quantityFor(cart, line) {
    if (!validCart(cart) || !line.comparable) return null;
    return cart.items.filter(item => String(item.variant_id || item.id) === line.id && planId(item) === String(line.selling_plan) && propertyKey(item.properties) === propertyKey(line.properties)).reduce((sum, item) => sum + item.quantity, 0);
  }
  function confirmedLine(json, line) {
    const items = Array.isArray(json?.items) ? json.items : [json];
    return items.length === 1 && items[0] && String(items[0].variant_id || items[0].id) === line.id && Number.isInteger(items[0].quantity) && items[0].quantity >= line.quantity && planId(items[0]) === String(line.selling_plan) && propertyKey(items[0].properties) === propertyKey(line.properties);
  }
  function createClient(options = {}) {
    const request = options.fetch || ((...args) => fetch(...args));
    const storage = options.storage;
    const storageKey = 'scp:cart-add-review';
    let busy = false, review = false;
    try { review = !!storage?.getItem(storageKey); } catch {}
    function markReview(value) { review = value; try { if (value) storage?.setItem(storageKey, '1'); else storage?.removeItem(storageKey); } catch {} }
    function requiresReview() {
      if (storage) { try { review = storage.getItem(storageKey) != null; } catch {} }
      return review;
    }
    async function jsonRequest(url, init, timeout) {
      const controller = new AbortController(); let timer;
      const work = (async () => {
        const response = await request(url, { ...init, credentials: 'same-origin', signal: controller.signal });
        let json = null;
        try { json = await response.json(); } catch {}
        return { ok: response.ok, status: response.status, json };
      })();
      try { return await Promise.race([work, new Promise((resolve, reject) => { timer = setTimeout(() => { controller.abort(); reject(new Error('request_timeout')); }, timeout); })]); }
      finally { clearTimeout(timer); }
    }
    async function readCart(url) {
      const result = await jsonRequest(url, { cache: 'no-store', headers: { Accept: 'application/json' } }, options.readTimeout || 5000);
      if (!result.ok || !validCart(result.json)) throw new Error('cart_unavailable');
      return result.json;
    }
    async function add(body, urls) {
      if (busy) return { code: 'busy' };
      if (requiresReview()) return { code: 'review_required', reviewRequired: true };
      busy = true;
      let sent = false, before;
      const line = snapshot(body);
      try {
        try { before = await readCart(urls.cart); } catch { return { code: 'before_unavailable', retrySafe: true }; }
        sent = true; markReview(true);
        let response;
        try { response = await jsonRequest(urls.add, { method: 'POST', body, headers: { 'X-Requested-With': 'XMLHttpRequest' } }, options.addTimeout || 10000); } catch {}
        const added = !!response?.ok && confirmedLine(response.json, line);
        let cart;
        try { cart = await readCart(urls.cart); } catch {}
        if (added) {
          markReview(false);
          const section = response.json.sections?.['mini-cart'];
          const previousQuantity = quantityFor(before, line), currentQuantity = quantityFor(cart, line);
          const rendered = !!cart && previousQuantity !== null && currentQuantity >= previousQuantity + line.quantity && !!options.validSection?.(section);
          return { code: rendered ? 'added' : 'added_refresh_failed', added: true, cart, sections: rendered ? response.json.sections : null, variant: response.json.items?.[0] || response.json };
        }
        const previousQuantity = quantityFor(before, line), currentQuantity = quantityFor(cart, line);
        if (response?.status === 422 && response.json?.status === 422 && previousQuantity !== null && currentQuantity === previousQuantity) {
          markReview(false);
          return { code: 'stock_rejected', retrySafe: true, cart };
        }
        const changed = previousQuantity !== null && currentQuantity !== null && currentQuantity !== previousQuantity;
        return { code: response?.status === 422 && changed ? 'stock_changed' : changed ? 'basket_changed' : 'uncertain', cart, reviewRequired: true };
      } catch {
        return { code: sent ? 'uncertain' : 'before_unavailable', reviewRequired: sent, retrySafe: !sent };
      } finally { busy = false; }
    }
    return { add, isBusy: () => busy, requiresReview };
  }
  function install(window) {
    let storage;
    try { storage = window.sessionStorage; if (window.themeVariables?.settings?.pageType === 'cart') storage.removeItem('scp:cart-add-review'); } catch {}
    const client = createClient({ storage, validSection: html => {
      if (typeof html !== 'string' || !html.trim()) return false;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      return !!parsed.querySelector('cart-drawer .drawer__content');
    } });
    const coordinator = window.scpCartMutationsClient;
    if (!coordinator) return client;
    return { ...client, localRequiresReview: client.requiresReview, requiresReview: () => client.requiresReview() || coordinator.reviewRequired(), add: (body, urls) => coordinator.exclusive(() => coordinator.reviewRequired() ? { code: 'review_required' } : client.add(body, urls)), isBusy: () => client.isBusy() || coordinator.isBusy() };
  }
  return { createClient, snapshot, quantityFor, confirmedLine, validCart, install };
});
